import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Trophy, Dumbbell, Utensils, Heart, Moon, Flame, 
  Droplet, Crown, Zap, Gift, Calendar, CheckCircle,
  Clock, Star
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const ICON_MAP = {
  dumbbell: Dumbbell,
  utensils: Utensils,
  heart: Heart,
  moon: Moon,
  flame: Flame,
  droplet: Droplet,
  crown: Crown,
  zap: Zap
};

const CATEGORY_COLORS = {
  training: 'from-red-500/20 to-red-600/10 border-red-500/30',
  nutrition: 'from-green-500/20 to-green-600/10 border-green-500/30',
  recovery: 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
  mental: 'from-purple-500/20 to-purple-600/10 border-purple-500/30'
};

const CATEGORY_ICON_COLORS = {
  training: 'text-red-400 bg-red-500/20',
  nutrition: 'text-green-400 bg-green-500/20',
  recovery: 'text-blue-400 bg-blue-500/20',
  mental: 'text-purple-400 bg-purple-500/20'
};

const WeeklyChallengesPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [weeklyData, setWeeklyData] = useState(null);
  const [claiming, setClaiming] = useState(null);

  useEffect(() => {
    fetchWeeklyChallenges();
  }, []);

  const fetchWeeklyChallenges = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/challenges/weekly`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeeklyData(response.data);
    } catch (error) {
      console.error('Failed to fetch weekly challenges:', error);
      toast.error('Failed to load weekly challenges');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimReward = async (challengeId) => {
    setClaiming(challengeId);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(
        `${API}/challenges/weekly/claim`,
        { challenge_id: challengeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchWeeklyChallenges();
    } catch (error) {
      if (error.response?.data?.detail === 'Challenge already claimed') {
        toast.info('Already claimed!');
      } else {
        toast.error('Failed to claim reward');
      }
    } finally {
      setClaiming(null);
    }
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options = { month: 'short', day: 'numeric' };
    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
  };

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff + 1);
  };

  if (loading) {
    return (
      <DashboardLayout title="Weekly Challenges">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-12 h-12 border-4 border-aura-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const daysRemaining = weeklyData ? getDaysRemaining(weeklyData.week_end) : 0;

  return (
    <DashboardLayout title="Weekly Challenges">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
              Weekly Challenges
            </h1>
            {weeklyData && (
              <p className="text-aura-text-muted mt-1 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDateRange(weeklyData.week_start, weeklyData.week_end)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-aura-secondary/20 text-aura-secondary border-0 px-3 py-1.5">
              <Clock className="w-4 h-4 mr-1" />
              {daysRemaining} days left
            </Badge>
          </div>
        </div>

        {/* Points Summary */}
        <Card className="bg-gradient-to-r from-aura-primary/20 to-aura-secondary/10 border-aura-primary/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-aura-primary/20 rounded-xl flex items-center justify-center">
                  <Trophy className="w-7 h-7 text-aura-primary" />
                </div>
                <div>
                  <p className="text-sm text-aura-text-muted uppercase tracking-wider">Points Earned This Week</p>
                  <p className="font-heading font-bold text-3xl text-white">
                    {weeklyData?.points_earned || 0}
                    <span className="text-lg text-aura-text-muted ml-2">
                      / {weeklyData?.challenges?.reduce((sum, c) => sum + c.points, 0) || 0}
                    </span>
                  </p>
                </div>
              </div>
              {weeklyData?.all_complete && (
                <Badge className="bg-green-500/20 text-green-400 border-0 text-sm px-4 py-2">
                  <Star className="w-4 h-4 mr-1" />
                  All Complete!
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Challenges Grid */}
        <div className="grid gap-4">
          {weeklyData?.challenges?.map((challenge, index) => {
            const Icon = ICON_MAP[challenge.icon] || Trophy;
            const colorClass = CATEGORY_COLORS[challenge.category] || CATEGORY_COLORS.training;
            const iconColorClass = CATEGORY_ICON_COLORS[challenge.category] || CATEGORY_ICON_COLORS.training;
            const canClaim = challenge.progress >= challenge.target && !challenge.completed;

            return (
              <motion.div
                key={challenge.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`bg-gradient-to-br ${colorClass} border overflow-hidden`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${iconColorClass}`}>
                        <Icon className="w-7 h-7" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-heading font-bold text-lg text-white uppercase tracking-tight">
                            {challenge.name}
                          </h3>
                          {challenge.completed && (
                            <CheckCircle className="w-5 h-5 text-green-400" />
                          )}
                        </div>
                        <p className="text-sm text-aura-text-muted mb-3">
                          {challenge.description}
                        </p>

                        {/* Progress */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-aura-text-muted">Progress</span>
                            <span className="font-medium text-white">
                              {challenge.progress} / {challenge.target}
                            </span>
                          </div>
                          <Progress 
                            value={challenge.percentage} 
                            className="h-2 bg-white/10"
                          />
                        </div>
                      </div>

                      {/* Reward Section */}
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-0 font-bold">
                          +{challenge.points} pts
                        </Badge>
                        
                        {challenge.completed ? (
                          <Badge className="bg-green-500/20 text-green-400 border-0">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Claimed
                          </Badge>
                        ) : canClaim ? (
                          <Button
                            size="sm"
                            onClick={() => handleClaimReward(challenge.id)}
                            disabled={claiming === challenge.id}
                            className="bg-aura-primary hover:bg-aura-primary-hover text-white"
                            data-testid={`claim-${challenge.id}`}
                          >
                            {claiming === challenge.id ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>
                                <Gift className="w-4 h-4 mr-1" />
                                Claim
                              </>
                            )}
                          </Button>
                        ) : (
                          <span className="text-xs text-aura-text-subtle">
                            {challenge.target - challenge.progress} more to go
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Tips Section */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-400" />
              Pro Tips
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="font-medium text-white mb-2">Stack Your Progress</p>
                <p className="text-sm text-aura-text-muted">
                  Complete daily challenges alongside weekly goals to maximize your Aura points.
                </p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="font-medium text-white mb-2">Claim Before Reset</p>
                <p className="text-sm text-aura-text-muted">
                  Weekly challenges reset every Monday. Make sure to claim your rewards!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default WeeklyChallengesPage;
